import server from 'api';

// Client of /v03/signatures (gastronaut-api serverV03/Routes/Signatures): waiter
// signatures („Service-Kürzel“) and their PINs. The server owns the PIN state and
// decides every check; the dashboard sends a PIN only to the server and never sees
// a verifier. Signature documents stay readable from Firestore (name, hasPin).

export type SignatureInfo = { signature: string; name: string; hasPin: boolean };

/** legacy: not prepared for the ticket door; preparing: being prepared (locked); ready. */
export type SignatureSetupState = 'legacy' | 'preparing' | 'ready';

export type SignatureFailure = {
  status: number;
  code: string;
  message: string;
  /** No answer or a 5xx: whether the server applied it is unknown (a repeat is safe). */
  uncertain?: boolean;
  remaining?: number;
  retryAfter?: number;
  existingName?: string;
};

const EXPECTED = [400, 403, 404, 409, 429];
const base = (restaurantId: string) => `/v03/signatures/${encodeURIComponent(restaurantId)}`;
const one = (restaurantId: string, signature: string) => `${base(restaurantId)}/${encodeURIComponent(signature)}`;

export const signatureFailure = (error: any): SignatureFailure => {
  const response = error?.response;
  if (!response || response.status >= 500) {
    return {
      status: response?.status ?? 0,
      code: response ? 'server_error' : 'network',
      uncertain: true,
      message: 'Speichern nicht bestätigt (keine Verbindung zum Server). Bitte erneut versuchen – ein bereits gespeichertes Kürzel wird dabei erkannt.',
    };
  }
  return {
    ...(response.data ?? {}),
    status: response.status,
    code: response.data?.code ?? 'error',
    message: response.data?.message ?? 'Unbekannter Fehler.',
  };
};

export const signatureApi = {
  /** Whether this restaurant's Kürzel are prepared for the ticket door, and whether this account may do it. */
  setup: (restaurantId: string) =>
    server
      .get<{ state: SignatureSetupState; canPrepare: boolean }>(`${base(restaurantId)}/setup`, { expectedErrorStatuses: EXPECTED })
      .then((r) => r.data),

  /** „Service-Kürzel für den Einlass vorbereiten“ (restaurant admins; repeatable, resumes an interrupted run). */
  prepare: (restaurantId: string) =>
    server
      .post<{ state: 'ready'; alreadyReady: boolean; signatures: number; needNewPin: number }>(`${base(restaurantId)}/prepare`, {}, {
        expectedErrorStatuses: EXPECTED,
        // Converting many Kürzel takes longer than an ordinary request.
        timeout: 60_000,
      })
      .then((r) => r.data),

  /** Create-only; repeating the same creation after a lost answer is recognised (`created: false`). */
  create: (restaurantId: string, body: { signature: string; name: string; pin?: string | null }) =>
    server
      .post<SignatureInfo & { created: boolean }>(base(restaurantId), body, { expectedErrorStatuses: EXPECTED })
      .then((r) => r.data),

  /** The PIN prompts' check (counted per account; locked for a minute after 5 failures). */
  verify: (restaurantId: string, signature: string, pin: string) =>
    server
      .post<SignatureInfo>(`${one(restaurantId, signature)}/verify`, { pin }, { expectedErrorStatuses: EXPECTED })
      .then((r) => r.data),

  /** Name and/or PIN (`pin`: new, null = remove). Needs the current PIN, unless restaurant admin or none is set. */
  change: (restaurantId: string, signature: string, body: { name?: string; pin?: string | null; currentPin?: string }) =>
    server
      .patch<SignatureInfo>(one(restaurantId, signature), body, { expectedErrorStatuses: EXPECTED })
      .then((r) => r.data),

  /** Restaurant admins only. */
  remove: (restaurantId: string, signature: string) =>
    server.delete<{ signature: string; deleted: boolean }>(one(restaurantId, signature), { expectedErrorStatuses: EXPECTED }).then((r) => r.data),
};
